import { Helmet } from "react-helmet";
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  Paper,
  Typography,
  Link as MuiLink,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { useDebounce } from "../hooks/useDebounce";
import {
  fetchReviewLocationsInBounds,
  MapBounds,
  ReviewLocationSummary,
} from "../helpers/api";
import {
  getMapBoundsCached,
  setMapBoundsCached,
} from "../helpers/mapBoundsCache";

/** Downtown Los Angeles — used when geolocation is denied or unavailable */
const FALLBACK_MAP_CENTER = { lat: 34.0522, lng: -118.2437 };

function escapeHtml(text: string): string {
  const div = document.createElement("div");
  div.textContent = text;
  return div.innerHTML;
}

const MapPage: React.FC = () => {
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const markersRef = useRef<google.maps.Marker[]>([]);
  const infoWindowRef = useRef<google.maps.InfoWindow | null>(null);
  const idleListenerRef = useRef<google.maps.MapsEventListener | null>(null);
  /** Skip setBoundsQuery when map idle repeats with the same viewport (reduces debounce churn). */
  const lastBoundsKeyRef = useRef<string | null>(null);

  const [mapsReady, setMapsReady] = useState(!!window.google?.maps);
  const [mapCenter, setMapCenter] = useState(FALLBACK_MAP_CENTER);
  const mapCenterRef = useRef(mapCenter);
  mapCenterRef.current = mapCenter;

  const [boundsQuery, setBoundsQuery] = useState<MapBounds | null>(null);
  const debouncedBounds = useDebounce(boundsQuery, 450);

  const [locations, setLocations] = useState<ReviewLocationSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [mapError, setMapError] = useState<string | null>(null);

  // Wait for Maps script (loaded async in index.html)
  useEffect(() => {
    if (window.google?.maps) {
      setMapsReady(true);
      return;
    }
    const t = window.setInterval(() => {
      if (window.google?.maps) {
        setMapsReady(true);
        window.clearInterval(t);
      }
    }, 150);
    const timeout = window.setTimeout(() => {
      window.clearInterval(t);
      if (!window.google?.maps) {
        setMapError(
          "Google Maps failed to load. Check your network and API key configuration."
        );
      }
    }, 15000);
    return () => {
      window.clearInterval(t);
      window.clearTimeout(timeout);
    };
  }, []);

  // Ask once for the user’s location to center the map; browser shows the permission prompt.
  useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setMapCenter({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
        });
      },
      () => {
        /* keep FALLBACK_MAP_CENTER (Los Angeles) */
      },
      {
        enableHighAccuracy: true,
        timeout: 15_000,
        maximumAge: 120_000,
      }
    );
  }, []);

  const applyBoundsToState = useCallback((map: google.maps.Map) => {
    const b = map.getBounds();
    if (!b) return;
    const ne = b.getNorthEast();
    const sw = b.getSouthWest();
    const north = ne.lat();
    const south = sw.lat();
    const east = ne.lng();
    const west = sw.lng();
    const key = [south, north, west, east].map((n) => n.toFixed(5)).join("|");
    if (lastBoundsKeyRef.current === key) return;
    lastBoundsKeyRef.current = key;
    setBoundsQuery({ north, south, east, west });
  }, []);

  useEffect(() => {
    if (!mapsReady || !mapContainerRef.current || mapRef.current) return;

    const containerEl = mapContainerRef.current;
    let resizeObserver: ResizeObserver | null = null;

    try {
      const center = { ...mapCenterRef.current };
      const map = new window.google.maps.Map(containerEl, {
        center,
        // Start zoomed in (~neighborhood) so bounds stay under API max span without zooming first
        zoom: 16,
        minZoom: 9,
        mapTypeControl: true,
        streetViewControl: false,
        fullscreenControl: true,
        keyboardShortcuts: true,
      });
      mapRef.current = map;
      infoWindowRef.current = new window.google.maps.InfoWindow();

      idleListenerRef.current = map.addListener("idle", () => {
        applyBoundsToState(map);
      });

      const triggerResize = () => {
        if (mapRef.current) {
          window.google.maps.event.trigger(mapRef.current, "resize");
        }
      };
      resizeObserver = new ResizeObserver(() => {
        triggerResize();
      });
      resizeObserver.observe(containerEl);
      requestAnimationFrame(triggerResize);
    } catch (e) {
      console.error(e);
      setMapError("Could not initialize the map.");
    }

    return () => {
      resizeObserver?.disconnect();
      if (window.google?.maps && idleListenerRef.current) {
        window.google.maps.event.removeListener(idleListenerRef.current);
      }
      idleListenerRef.current = null;
      markersRef.current.forEach((m) => m.setMap(null));
      markersRef.current = [];
      infoWindowRef.current = null;
      mapRef.current = null;
    };
  }, [mapsReady, applyBoundsToState]);

  // After geolocation resolves (or on prop changes), recenter without tearing down the map.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !window.google?.maps) return;
    const cur = map.getCenter();
    if (
      cur &&
      Math.abs(cur.lat() - mapCenter.lat) < 1e-8 &&
      Math.abs(cur.lng() - mapCenter.lng) < 1e-8
    ) {
      return;
    }
    map.setCenter(mapCenter);
  }, [mapCenter]);

  useEffect(() => {
    if (!debouncedBounds) {
      setLoading(false);
      return;
    }

    const ac = new AbortController();
    let cancelled = false;

    const cached = getMapBoundsCached(debouncedBounds);
    if (cached != null) {
      setLocations(cached);
      setFetchError(null);
      setLoading(false);
      return () => {
        cancelled = true;
        ac.abort();
      };
    }

    setLoading(true);
    setFetchError(null);
    fetchReviewLocationsInBounds(debouncedBounds, { signal: ac.signal })
      .then((locs) => {
        if (cancelled) return;
        setLocations(locs);
        setMapBoundsCached(debouncedBounds, locs);
      })
      .catch((err: unknown) => {
        if (cancelled || (err instanceof DOMException && err.name === "AbortError")) {
          return;
        }
        const message =
          err instanceof Error ? err.message : "Could not load locations.";
        setLocations([]);
        setFetchError(message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
      ac.abort();
      setLoading(false);
    };
  }, [debouncedBounds]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !window.google?.maps) return;

    markersRef.current.forEach((m) => m.setMap(null));
    markersRef.current = [];

    const iw = infoWindowRef.current;
    if (!iw) return;

    locations.forEach((loc) => {
      const marker = new window.google.maps.Marker({
        position: { lat: Number(loc.latitude), lng: Number(loc.longitude) },
        map,
        title: loc.address,
      });
      const reviewsUrl = `/reviews?address=${encodeURIComponent(loc.address)}`;
      marker.addListener("click", () => {
        iw.setContent(
          `<div style="max-width:240px;font-family:system-ui,sans-serif;padding:4px 2px;">
            <strong>${escapeHtml(loc.address)}</strong>
            <p style="margin:8px 0 4px;font-size:14px;">${loc.review_count} review${
            loc.review_count === 1 ? "" : "s"
          }</p>
            <a href="${reviewsUrl}" style="font-weight:600;color:#1d4ed8;">View reviews</a>
          </div>`
        );
        iw.open({ map, anchor: marker });
      });
      markersRef.current.push(marker);
    });
  }, [locations]);

  return (
    <>
      <Helmet>
        <title>Review Map | Rate My Cribs</title>
        <meta
          name="description"
          content="Explore a map of rental properties that have tenant reviews. Pan and zoom to see reviewed addresses in your area."
        />
        <link rel="canonical" href="https://ratemycribs.com/map" />
      </Helmet>
      <Box
        component="main"
        id="main-content"
        tabIndex={-1}
        sx={{
          flex: 1,
          width: "100%",
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          py: { xs: 2, sm: 3 },
          px: { xs: 1.5, sm: 2 },
        }}
      >
        <Paper
          elevation={0}
          sx={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            borderRadius: 2,
            border: "1px solid",
            borderColor: "divider",
            overflow: "hidden",
            maxWidth: 1200,
            width: "100%",
            mx: "auto",
            maxHeight: {
              xs: "calc(100dvh - 148px)",
              sm: "calc(100dvh - 156px)",
              md: "calc(100dvh - 168px)",
            },
          }}
        >
          <Box
            sx={{
              px: { xs: 2.75, sm: 3.5 },
              pt: { xs: 2.5, sm: 3 },
              pb: { xs: 2.25, sm: 2.75 },
              borderBottom: 1,
              borderColor: "divider",
            }}
          >
            <Typography
              variant="h5"
              component="h1"
              fontWeight={800}
              color="primary"
              sx={{ mb: 1.25, letterSpacing: "-0.01em" }}
            >
              Review map
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                lineHeight: 1.65,
                maxWidth: "58ch",
              }}
            >
              Pins update as you pan or zoom—tap one for review links. We use your location
              once to center the map (Los Angeles if blocked). Zoom in if the view is empty;
              very large areas are skipped for performance.
            </Typography>
          </Box>

          {mapError && (
            <Alert
              severity="error"
              sx={{ mx: { xs: 2.5, sm: 3 }, mt: 2, mb: 0.5 }}
            >
              {mapError}
            </Alert>
          )}

          {fetchError && (
            <Alert
              severity="warning"
              sx={{ mx: { xs: 2.5, sm: 3 }, mt: 2, mb: 0.5 }}
            >
              {fetchError}
            </Alert>
          )}

          <Box
            sx={{
              position: "relative",
              flex: 1,
              minHeight: { xs: 300, sm: 360 },
              minWidth: 0,
              overflow: "hidden",
            }}
          >
            {loading && (
              <Box
                sx={{
                  position: "absolute",
                  top: 12,
                  right: 12,
                  zIndex: 2,
                  bgcolor: "background.paper",
                  borderRadius: 1,
                  boxShadow: 2,
                  px: 1.5,
                  py: 1,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
                aria-live="polite"
              >
                <CircularProgress size={20} aria-hidden />
                <Typography variant="caption">Updating…</Typography>
              </Box>
            )}
            <Box
              ref={mapContainerRef}
              role="application"
              aria-label="Interactive map of reviewed rental properties"
              sx={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
              }}
            />
          </Box>

          <Box
            sx={{
              px: { xs: 2.75, sm: 3.5 },
              py: { xs: 1.75, sm: 2.25 },
              borderTop: 1,
              borderColor: "divider",
              display: "flex",
              flexWrap: "wrap",
              gap: 1.25,
              alignItems: "center",
              justifyContent: "space-between",
              rowGap: 1,
            }}
          >
            <Typography variant="caption" color="text.secondary">
              {debouncedBounds
                ? `${locations.length} location${
                    locations.length === 1 ? "" : "s"
                  } in view`
                : "Move the map to load locations"}
            </Typography>
            <MuiLink component={RouterLink} to="/reviews" variant="caption" fontWeight={700}>
              Search reviews by address
            </MuiLink>
          </Box>
        </Paper>
      </Box>
    </>
  );
};

export default MapPage;
